'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["transitionProps"],
  _excluded2 = ["modal", "sandboxed", "children"],
  _excluded3 = ["align", "animate", "children", "className", "currentPanel", "modal", "onOpenStart", "onOpenComplete", "onCloseStart", "onCloseComplete", "onPreviousClick", "panelOrder", "style"];
import classNames from 'classnames';
import { PANEL_SHADOW_COLOR } from 'HubStyleTokens/theme';
import { PANEL_NAVIGATOR_TRANSITION_TIMING } from 'HubStyleTokens/times';
import PropTypes from 'prop-types';
import { Children, cloneElement, Component } from 'react';
import invariant from 'react-utils/invariant';
import memoizeOne from 'react-utils/memoizeOne';
import styled from 'styled-components';
import * as PanelNavigatorContext from '../context/PanelNavigatorContext';
import { SandboxContext } from '../context/SandboxContext';
import UIDialogBackButton from '../dialog/UIDialogBackButton';
import UIModalPanel from '../dialog/UIModalPanel';
import UILayer from '../layer/UILayer';
import PanelNavigatorTransition from './internal/PanelNavigatorTransition';
import PanelTransition from './internal/PanelTransition';
import { getPanelTransitionProps } from './internal/panelUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const TRANSITION_TIMING = parseInt(PANEL_NAVIGATOR_TRANSITION_TIMING, 10);
const NonModalLayer = props => {
  return /*#__PURE__*/_jsx(UILayer, Object.assign({}, props, {
    Transition: PanelTransition
  }));
};
const ModalLayer = props => {
  const {
      transitionProps
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(UIModalPanel, Object.assign({
    width: "auto",
    onCloseComplete: transitionProps.onCloseComplete
  }, rest));
};
const Layer = props => {
  const {
      modal,
      sandboxed,
      children
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  if (sandboxed) {
    return /*#__PURE__*/_jsx("div", {
      style: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0
      },
      children: children
    });
  }
  const LayerComponent = modal ? ModalLayer : NonModalLayer;
  return /*#__PURE__*/_jsx(LayerComponent, Object.assign({}, rest, {
    children: children
  }));
};
const PanelNavigator = styled.div.withConfig({
  displayName: "UIPanelNavigator__PanelNavigator",
  componentId: "oy6tfc-0"
})(["height:100%;overflow-x:hidden;overflow-y:auto;box-shadow:", "px 0 8px 0 ", ";"], ({
  align
}) => align === 'left' ? 3 : -3, PANEL_SHADOW_COLOR);
class UIPanelNavigator extends Component {
  static getDerivedStateFromProps(props, state) {
    const {
      children,
      currentPanel: nextPanel,
      panelOrder
    } = props;
    const {
      animateForward,
      prevCurrentPanel: currentPanel
    } = state;
    if (nextPanel !== currentPanel) {
      let nextPanelIndex = -1;
      let currentPanelIndex = -1;
      if (panelOrder) {
        nextPanelIndex = panelOrder.indexOf(nextPanel);
        currentPanelIndex = panelOrder.indexOf(currentPanel);
      }
      if (nextPanelIndex === -1 || currentPanelIndex === -1) {
        Children.forEach(children, (child, i) => {
          if (child.props.panelKey === nextPanel) nextPanelIndex = i;
          if (child.props.panelKey === currentPanel) currentPanelIndex = i;
        });
      }
      const shouldAnimateForward = nextPanelIndex > currentPanelIndex;
      if (shouldAnimateForward !== animateForward) {
        return {
          animateForward: shouldAnimateForward,
          prevCurrentPanel: props.currentPanel
        };
      }
      return {
        prevCurrentPanel: props.currentPanel
      };
    }
    return null;
  }
  constructor(props) {
    super(props);
    this.getCurrentPanel = () => {
      const {
        currentPanel,
        children,
        align
      } = this.props;
      const panel = Children.toArray(children).find(child => child.props.panelKey === currentPanel);
      if (panel) {
        const {
          className
        } = panel.props;
        const classes = classNames('private-panel-navigator--child', className);
        return /*#__PURE__*/cloneElement(panel, {
          _dontMakeNewLayer: true,
          key: panel.props.panelKey,
          align,
          className: classes
        });
      }
      return panel;
    };
    this.renderPreviousButton = panelKey => {
      const {
        children,
        onPreviousClick,
        panelOrder
      } = this.props;
      const isRootPanel = panelOrder ? panelOrder.indexOf(panelKey) === 0 : Children.toArray(children)[0].props.panelKey === panelKey;
      if (isRootPanel) return null;
      return /*#__PURE__*/_jsx(UIDialogBackButton, {
        onClick: onPreviousClick
      });
    };
    this.state = {
      animateForward: true,
      prevCurrentPanel: props.currentPanel
    };
    this._panelNavigatorCtxValue = {
      getCurrentPanel: () => this.props.currentPanel,
      renderNavigation: this.renderPreviousButton
    };
    this._getTransitionProps = memoizeOne(getPanelTransitionProps);
  }
  render() {
    const _this$props = this.props,
      {
        align,
        animate,
        children,
        className,
        modal,
        onOpenStart,
        onOpenComplete,
        onCloseStart,
        onCloseComplete,
        style
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded3);
    const {
      animateForward
    } = this.state;
    const {
      sandboxed
    } = this.context;
    const currentPanel = this.getCurrentPanel();
    if (process.env.NODE_ENV !== 'production') {
      Children.forEach(children, child => {
        invariant(typeof child.props._dontMakeNewLayer === 'boolean', 'UIPanelNavigator: Children should normally be `UIPanel` instances. To silence this ' + 'error, define a boolean value for `_dontMakeNewLayer` on each child.');
        invariant(typeof child.props.panelKey === 'string', 'UIPanelNavigator: Each child must have a `panelKey`.');
        invariant(child.props.width != null, 'UIPanelNavigator: Each child must have a `width`.');
      });
    }
    return /*#__PURE__*/_jsx(PanelNavigatorContext.Provider, {
      value: this._panelNavigatorCtxValue,
      children: /*#__PURE__*/_jsx(Layer, Object.assign({
        "data-layer-for": "UIPanelNavigator",
        modal: modal,
        sandboxed: sandboxed,
        transitionProps: this._getTransitionProps(onOpenStart, onOpenComplete, onCloseStart, onCloseComplete, align, animate)
      }, rest, {
        children: /*#__PURE__*/_jsx(PanelNavigator, {
          align: align,
          className: className,
          style: style,
          children: /*#__PURE__*/_jsx(PanelNavigatorTransition, {
            duration: TRANSITION_TIMING,
            reverse: !animateForward,
            children: currentPanel
          })
        })
      }))
    });
  }
}
UIPanelNavigator.displayName = 'UIPanelNavigator';
UIPanelNavigator.contextType = SandboxContext;
UIPanelNavigator.defaultProps = {
  align: 'right',
  animate: true,
  modal: false
};
UIPanelNavigator.propTypes = {
  align: PropTypes.oneOf(['left', 'right']),
  animate: PropTypes.bool.isRequired,
  children: PropTypes.arrayOf(PropTypes.node),
  currentPanel: PropTypes.string.isRequired,
  modal: PropTypes.bool.isRequired,
  onCloseComplete: PropTypes.func,
  onCloseStart: PropTypes.func,
  onOpenComplete: PropTypes.func,
  onOpenStart: PropTypes.func,
  panelOrder: PropTypes.arrayOf(PropTypes.string),
  onPreviousClick: PropTypes.func,
  rootNode: UILayer.propTypes.rootNode
};
export default UIPanelNavigator;